class HigroovPurchaseOptions extends HTMLElement {
  connectedCallback() {
    this.abortController?.abort();
    this.abortController = new AbortController();

    this.form = this.closest('product-form-component')?.querySelector('form');
    this.quantityInput = this.form?.querySelector('input[name="quantity"]');
    this.sellingPlanInput = this.form?.querySelector('[data-higroov-selling-plan]');
    this.status = this.querySelector('[data-selection-status]');
    this.oneTimeTotal = this.querySelector('[data-one-time-total]');
    this.subscriptionTotal = this.querySelector('[data-subscription-mode-total]');
    this.subscriptionPanelTotal = this.querySelector('[data-subscription-panel-total]');
    this.subscriptionUnitPriceLabel = this.querySelector('[data-subscription-unit-price]');
    this.subscriptionQuantityInput = this.querySelector('[data-subscription-quantity]');
    this.subscriptionDecrease = this.querySelector('[data-subscription-decrease]');
    this.subscriptionIncrease = this.querySelector('[data-subscription-increase]');
    this.subscriptionSchedule = this.querySelector('[data-subscription-schedule]');
    this.bundleNote = this.querySelector('[data-bundle-note]');
    this.addToCartPrice = this.form?.querySelector('[ref="addToCartButton"] .prod_item');
    this.subscriptionUnitPrice = Number.parseInt(this.dataset.subscriptionUnitPrice, 10) || 0;
    this.currency = this.dataset.currency || 'USD';

    if (!this.form || !this.quantityInput || !this.sellingPlanInput || !this.subscriptionQuantityInput) return;

    const { signal } = this.abortController;
    this.querySelectorAll('[data-purchase-mode], [data-quantity-tier], [data-selling-plan-option]').forEach((input) => {
      input.addEventListener('change', () => {
        const selectedModeOption = input.matches('[data-purchase-mode]')
          ? input.closest('[data-mode-option]')
          : null;
        const shouldPreservePosition = input.value === 'subscription'
          && window.matchMedia('(max-width: 479px)').matches
          && selectedModeOption;

        this.applySelection();

        if (shouldPreservePosition) {
          this.keepModeInView(selectedModeOption);
        }
      }, { signal });
    });

    this.subscriptionDecrease?.addEventListener('click', () => {
      this.adjustSubscriptionQuantity(-1);
    }, { signal });
    this.subscriptionIncrease?.addEventListener('click', () => {
      this.adjustSubscriptionQuantity(1);
    }, { signal });
    this.subscriptionQuantityInput.addEventListener('input', () => {
      if (this.subscriptionQuantityInput.value !== '') this.applySelection();
    }, { signal });
    this.subscriptionQuantityInput.addEventListener('change', () => {
      this.applySelection();
    }, { signal });

    this.applySelection();
  }

  disconnectedCallback() {
    this.abortController?.abort();
    cancelAnimationFrame(this.positionFrame);
  }

  keepModeInView(option) {
    cancelAnimationFrame(this.positionFrame);
    this.positionFrame = requestAnimationFrame(() => {
      this.positionFrame = requestAnimationFrame(() => {
        if (!option?.isConnected) return;

        const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        option.scrollIntoView({
          block: 'center',
          behavior: reducedMotion ? 'auto' : 'smooth',
        });
      });
    });
  }

  adjustSubscriptionQuantity(delta) {
    const quantity = this.getSubscriptionQuantity();
    this.subscriptionQuantityInput.value = String(quantity + delta);
    this.applySelection();
    this.subscriptionQuantityInput.focus();
  }

  getSubscriptionQuantity() {
    const minimum = Number.parseInt(this.subscriptionQuantityInput.min, 10) || 1;
    const maximum = Number.parseInt(this.subscriptionQuantityInput.max, 10);
    const enteredQuantity = Number.parseInt(this.subscriptionQuantityInput.value, 10);
    let quantity = Number.isFinite(enteredQuantity) ? enteredQuantity : minimum;

    quantity = Math.max(minimum, quantity);
    if (Number.isFinite(maximum)) quantity = Math.min(maximum, quantity);

    this.subscriptionQuantityInput.value = String(quantity);
    return quantity;
  }

  formatMoney(cents, includeTrailingZeros = false) {
    return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
      style: 'currency',
      currency: this.currency,
      minimumFractionDigits: includeTrailingZeros ? 2 : 0,
      maximumFractionDigits: 2,
    }).format(cents / 100);
  }

  applySelection() {
    const mode = this.querySelector('[data-purchase-mode]:checked')?.value || 'one-time';
    const oneTimeTierInput = this.querySelector('[data-quantity-tier]:checked');
    const oneTimeTierOption = oneTimeTierInput?.closest('[data-tier-option]');
    const selectedPlanInput = this.querySelector('[data-selling-plan-option]:checked');
    const isSubscription = mode === 'subscription';
    const subscriptionQuantity = this.getSubscriptionQuantity();
    const quantity = isSubscription ? subscriptionQuantity : Number.parseInt(oneTimeTierInput?.value, 10) || 1;
    const bundleTotal = oneTimeTierOption?.dataset.tierTotal || '';
    const bundleButtonTotal = oneTimeTierOption?.dataset.tierButtonTotal || '';
    const bundleSavings = oneTimeTierOption?.dataset.tierSavings || '0';
    const selectedPlanId = selectedPlanInput?.value || this.dataset.sellingPlanId;
    const selectedPlanPrice = Number.parseInt(selectedPlanInput?.dataset.planPrice, 10)
      || this.subscriptionUnitPrice;
    const selectedPlanLabel = selectedPlanInput?.dataset.planLabel || 'Monthly';
    const selectedPlanSchedule = selectedPlanInput?.dataset.planSchedule || 'every month';
    const recurringTotalCents = selectedPlanPrice * subscriptionQuantity;
    const recurringTotal = this.formatMoney(recurringTotalCents);
    const recurringButtonTotal = this.formatMoney(recurringTotalCents, true);

    this.dataset.mode = mode;
    this.querySelector('[data-tier-panel]').hidden = isSubscription;
    this.querySelector('[data-subscription-tier-panel]').hidden = !isSubscription;

    this.querySelectorAll('[data-mode-option]').forEach((option) => {
      option.toggleAttribute('data-selected', option.querySelector('input')?.checked === true);
    });
    this.querySelectorAll('[data-tier-option]').forEach((option) => {
      option.toggleAttribute('data-selected', option.querySelector('input')?.checked === true);
    });
    this.querySelectorAll('[data-frequency-option]').forEach((option) => {
      option.toggleAttribute('data-selected', option.querySelector('input')?.checked === true);
    });

    this.sellingPlanInput.disabled = !isSubscription;
    this.sellingPlanInput.value = isSubscription ? selectedPlanId : '';
    this.quantityInput.value = String(quantity);
    this.quantityInput.dispatchEvent(new Event('change', { bubbles: true }));

    if (this.oneTimeTotal && bundleTotal) {
      this.oneTimeTotal.textContent = bundleTotal;
    }
    if (this.subscriptionTotal && recurringTotal) {
      this.subscriptionTotal.textContent = recurringTotal;
    }
    if (this.subscriptionPanelTotal && recurringTotal) {
      this.subscriptionPanelTotal.textContent = recurringTotal;
    }
    if (this.subscriptionUnitPriceLabel) {
      this.subscriptionUnitPriceLabel.textContent = `${this.formatMoney(selectedPlanPrice)} per bag`;
    }
    if (this.subscriptionSchedule) {
      this.subscriptionSchedule.textContent = `Billed and delivered ${selectedPlanSchedule}. Every bag is 25% off. Skip, pause, or cancel anytime.`;
    }

    const minimum = Number.parseInt(this.subscriptionQuantityInput.min, 10) || 1;
    const maximum = Number.parseInt(this.subscriptionQuantityInput.max, 10);
    if (this.subscriptionDecrease) this.subscriptionDecrease.disabled = subscriptionQuantity <= minimum;
    if (this.subscriptionIncrease) {
      this.subscriptionIncrease.disabled = Number.isFinite(maximum) && subscriptionQuantity >= maximum;
    }

    const buttonTotal = isSubscription ? recurringButtonTotal : bundleButtonTotal;
    if (this.addToCartPrice && buttonTotal) {
      this.addToCartPrice.textContent = ` ${buttonTotal} `;
    }

    if (this.bundleNote) {
      this.bundleNote.textContent = bundleSavings === '0'
        ? 'Standard one-time price. Bundle savings apply automatically in your cart.'
        : `${bundleSavings}% bundle savings selected. Your discount applies automatically in your cart.`;
    }

    if (this.status) {
      this.status.textContent = isSubscription
        ? `${selectedPlanLabel} subscription selected: ${quantity} ${quantity === 1 ? 'bag' : 'bags'} for ${recurringTotal} per delivery.`
        : `One-time purchase selected: ${quantity} ${quantity === 1 ? 'bag' : 'bags'}${bundleTotal ? ` for ${bundleTotal}` : ''}.`;
    }
  }
}

if (!customElements.get('higroov-purchase-options')) {
  customElements.define('higroov-purchase-options', HigroovPurchaseOptions);
}
